import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { cellarLabel } from '../auth/cellar-list';
import { useCellarList } from '../auth/CellarListContext';
import { sortedByLabel, t } from '../i18n';

/** What a link to the settings carries: the cellar the person was in. */
export interface SettingsLinkState {
  fromCellar?: string;
}

/**
 * The owner's cellar switcher (T071, FR-040), in the settings page since the owner's feedback of
 * 2026-09-28. A single drop-down (owner, 2026-10-03) listing the other cellars only: the one the
 * owner came from is left out (their own when unknown). Choosing one opens it, so the choice
 * lives in the address (two tabs never share a hidden "current cellar"), still two taps away
 * from any screen (SC-014). Never rendered for a member.
 */
export function CellarSelector({ labelledBy }: { labelledBy: string }) {
  const { cellars, refresh } = useCellarList();
  const { person } = useAuth();
  const navigate = useNavigate();
  const state = useLocation().state as SettingsLinkState | null;
  const current = state?.fromCellar ?? person?.id;
  const others = (cellars ?? []).filter((entry) => entry.id !== current);

  // Pick up a user who signed in for the first time since the list was loaded.
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <select
      className="cellar-selector"
      aria-labelledby={labelledBy}
      value=""
      onChange={(event) => {
        if (event.target.value !== '') navigate(`/c/${event.target.value}`);
      }}
    >
      <option value="">{t('cellar.choose')}</option>
      {sortedByLabel(others, cellarLabel).map((entry) => (
        <option key={entry.id} value={entry.id}>
          {cellarLabel(entry)}
        </option>
      ))}
    </select>
  );
}
